import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import {
  ContentState,
  RootState,
  updateContentList,
  updatePageSettings,
} from "@platformx/authoring-state";
import {
  AUTH_INFO,
  LanguageList,
  ShowToastError,
  ShowToastSuccess,
  appendBrandNameIfPresent,
  capitalizeFirstLetter,
  formatChildrenForPageDuplicate,
  formatPageUrl,
  getCurrentLang,
  getCurrentLangName,
  getSelectedBrandName,
  getSelectedSite,
  getSubDomain,
  isScheduledInFuture,
  setDefaultPageSettings,
  usePlatformAnalytics,
  useUserSession,
} from "@platformx/utilities";
import { addMinutes } from "date-fns";
import { SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { PageQueries } from "../../graphQL/queries/pageQueries";
import brandsApi from "../../services/brands/brands.api";
import contentTypeAPIs from "../../services/contentTypes/contentTypes.api";
import { fetchPageModel } from "../../services/page/page.api";
import { consolidatePageModel } from "./mapper";

const PageModelInstanceDefault = {
  Page: "",
  SiteName: "",
  Title: "",
  ParentPageURL: "/",
  CurrentPageURL: "/",
  DevelopedBy: "",
  DevelopedDate: "",
  IsEdit: false,
  SeoEnable: true,
  AnalyticsEnable: true,
  RobotTxt: false,
  SiteMap: false,
  Children: null,
  Analytics: "",
  Others: "",
  StructureData: "",
  PageSettings: {},
  Page_LastModificationDate: "",
};

const usePage = (filter = "ALL") => {
  const location = useLocation();
  const [getSession] = useUserSession();
  const { userInfo } = getSession();
  const { t, i18n } = useTranslation();
  const { page } = useSelector((state: RootState) => state);
  const [runFetchPageModel] = useLazyQuery(PageQueries.FETCH_PAGE_MODEL_DRAFT);
  const client = useApolloClient();
  const username = `${userInfo.first_name} ${userInfo.last_name}`;
  const { contentList, startIndex } = useSelector((state: ContentState) => state);
  const [handleImpression] = usePlatformAnalytics();
  const [mutate] = useMutation(PageQueries.CREATE_PAGE_MODEL, {
    context: {
      headers: {
        language: localStorage.getItem("lang"),
        sitename: getSelectedSite(),
      },
    },
  });
  const [mutateUnpublish] = useMutation(PageQueries.UNPUBLISH_PAGE);
  const [mutateDelete] = useMutation(PageQueries.DELETE_PAGE);
  const [currentPublishTime, setCurrentPublishTime] = useState("");
  const [currentUnpublishTime, setCurrentUnpublishTime] = useState("");
  const [mutatePublishSchedule] = useMutation(PageQueries.RESCHEDULE_PUBLISH);
  const [mutateUnpublishSchedule] = useMutation(PageQueries.RESCHEDULE_UNPUBLISH);
  const [mutateCancelPublishSchedule] = useMutation(PageQueries.CANCEL_PUBLISH);
  const [mutateCancelUnpublishSchedule] = useMutation(PageQueries.CANCEL_UNPUBLISH);
  const navigate = useNavigate();
  const [mutatePublish] = useMutation(PageQueries.PUBLISH_PAGE_MODEL);
  localStorage.setItem("lang", getCurrentLang());
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const cardClickHandle = useCallback(
    (
      parameter: string,
      status: string,
      pathForSelectedPage: string,
      actionType?: string,
      deviceType?: string,
      editOption?: string,
      searchCatURL?: string,
      searchTermURL?: string,
      sortByURL?: string,
    ) => {
      const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || i18n.language);
      const publishPageURL = `${getSubDomain()}/${selectedBrandRoute}/${parameter}`;
      if (status?.toLowerCase() === "draft" || status?.toLowerCase() === "unpublished") {
        fetchPageModel(
          dispatch,
          client,
          pathForSelectedPage,
          navigate,
          actionType,
          deviceType,
          editOption,
          searchCatURL,
          searchTermURL,
          sortByURL,
        );
      } else {
        window.open(publishPageURL);
      }
    },
    [],
  );

  /**edit page click handle here*/
  const editPage = (selectedPage: any) => {
    cardClickHandle(
      selectedPage.page || "",
      "draft",
      selectedPage.path || "",
      selectedPage.status === "draft" &&
        (selectedPage.scheduledPublishTriggerDateTime ||
          selectedPage.scheduledUnPublishTriggerDateTime)
        ? "preview"
        : "",
      selectedPage.status === "draft" &&
        (selectedPage.scheduledPublishTriggerDateTime ||
          selectedPage.scheduledUnPublishTriggerDateTime)
        ? "window"
        : "",
      selectedPage.status === "draft" &&
        (selectedPage.scheduledPublishTriggerDateTime ||
          selectedPage.scheduledUnPublishTriggerDateTime)
        ? "no"
        : "",
      "",
      "",
      "",
    );
  };

  /**view page click handle here */
  const viewPage = (selectedPage: any) => {
    cardClickHandle(
      selectedPage.slug || selectedPage.page || "",
      selectedPage.status === "published" ? "published" : "draft",
      selectedPage.path || "",
      "preview",
      "window",
      selectedPage.status === "draft" &&
        (selectedPage.scheduledPublishTriggerDateTime ||
          selectedPage.scheduledUnPublishTriggerDateTime)
        ? "no"
        : "yes",
      "",
      "",
      "",
    );
  };

  /**preview page click handle here */
  const previewPage = (selectedPage: any) => {
    cardClickHandle(
      selectedPage.slug || selectedPage.page || "",
      selectedPage.status === "published" ? "published" : "draft",
      selectedPage.path || "",
      "preview",
      "window",
      selectedPage.status === "draft" &&
        (selectedPage.scheduledPublishTriggerDateTime ||
          selectedPage.scheduledUnPublishTriggerDateTime)
        ? "no"
        : "yes",
      "",
      "",
      "",
    );
  };

  /**handle duplicate page popup data */
  const handleDuplicatePopup = (duplicate: any, pageSelected: { path: any; status: any }) => {
    if (duplicate) {
      fetchPageModel(dispatch, client, pageSelected.path, null, pageSelected.status);
    }
  };

  /**create page */
  const createPageModel = async (newPageModel: any, isDuplicate?: any, code?: any) => {
    await mutate({
      variables: { input: appendBrandNameIfPresent(newPageModel) },
      context: {
        headers: {
          language: localStorage.getItem("lang"),
          sitename: getSelectedSite(),
        },
      },
    })
      .then(async (resp) => {
        localStorage.removeItem("lang");
        if (code) {
          ShowToastSuccess(`${t("page")} ${t("created_toast")} ${t("for")} ${code}`);
        } else {
          ShowToastSuccess(`${t("page")} ${t("created_toast")}`);
        }
        localStorage.setItem("path", resp?.data?.authoring_createPage?.path);
        const pageDataObj = {
          eventType: "Successful Page Creation",
          pageCreated: true,
          created_page_title: newPageModel.Page,
          createdBy: newPageModel.DevelopedBy,
        };
        handleImpression(pageDataObj.eventType, pageDataObj);
        if (isDuplicate && code === getCurrentLangName()) {
          const searchResponse = await contentTypeAPIs.fetchSearchContent(
            capitalizeFirstLetter("sitepage"),
            location,
            filter,
            startIndex,
            contentList,
            true,
          );
          dispatch(updateContentList(searchResponse));
        }
        if (!isDuplicate) {
          navigate(
            {
              pathname: "/edit-page",
              search: `?${createSearchParams({
                page: resp?.data?.authoring_createPage?.path.toString(),
              })}`,
            },
            { state: "new" },
          );
        }
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;
        if (code) {
          ShowToastError(`${error.graphQLErrors[0].message} ${t("for")} ${code}`, statusCode);
        } else {
          ShowToastError(error.graphQLErrors[0].message, statusCode);
        }
      });
  };

  const createPageByName = async (pageName: any, pageUrl: any) => {
    const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || i18n.language);
    const newPageModel = JSON.parse(JSON.stringify(PageModelInstanceDefault));
    newPageModel.Page = pageUrl;
    newPageModel.Title = pageName;
    newPageModel.DevelopedBy = username;
    newPageModel.Page_LastModifiedBy = username;
    newPageModel.Page_LastModificationDate = new Date();
    newPageModel.DevelopedDate = new Date().toISOString();
    newPageModel.CurrentPageURL = `/${pageUrl}`;
    newPageModel.PageSettings = {
      PageName: pageName,
      PageDescription: "",
      PageTags: [],
      PageURL: `${getSubDomain()}/${selectedBrandRoute}/${pageUrl}`,
      PageViewer: "free",
      PageCaching: true,
      PageMobileFriendly: true,
      SeoKeywords: [],
      SeoTitle: pageName,
      SeoBlockIndexing: true,
    };
    newPageModel.SiteName = getSelectedSite();
    newPageModel.StructureData = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "WebPage",
      name: pageName,
      description: "",
      keywords: "",
      url: `${getSubDomain()}/${selectedBrandRoute}/${pageUrl}`,
    });
    createPageModel(newPageModel, false);
    dispatch(
      updatePageSettings(
        setDefaultPageSettings(
          pageName,
          undefined,
          undefined,
          `${AUTH_INFO.publishUri + selectedBrandRoute}/${pageUrl}`,
        ),
      ),
    );
    const searchResponse = await contentTypeAPIs.fetchSearchContent(
      capitalizeFirstLetter("sitepage"),
      location,
      filter,
      startIndex,
      contentList,
      true,
    );
    dispatch(updateContentList(searchResponse));
  };

  /**duplicate page */
  const duplicatePage = async (isDuplicate, pageName, pageUrl, language) => {
    if (pageName.trim().length > 0) {
      if (isDuplicate) {
        const copyPageModel = JSON.parse(JSON.stringify(page?.pageModel));
        const newPageModel = formatChildrenForPageDuplicate(
          copyPageModel,
          pageName,
          pageUrl,
          username,
        );
        language.forEach((lang) => {
          LanguageList().forEach((l: any) => {
            if (l.label === lang) {
              localStorage.setItem("lang", l.id);
              createPageModel(newPageModel, isDuplicate, l.label);
            }
          });
        });
      } else {
        await createPageByName(pageName, pageUrl);
      }
    }
  };

  /**remove page */
  const handleRemove = (
    itemsdata: { page: any; currentPageUrl: any; parentPageUrl: any },
    filterObject: any = {},
  ) => {
    const { filter: filterType = "ALL" } = filterObject || {};

    mutateDelete({
      variables: {
        page: itemsdata.page,
        currentpageurl: itemsdata.currentPageUrl,
        parentpageurl: itemsdata.parentPageUrl,
      },
    })
      .then(async () => {
        const searchResponse = await contentTypeAPIs.fetchSearchContent(
          capitalizeFirstLetter("sitepage"),
          location,
          filterType,
          startIndex,
          contentList,
          true,
        );
        dispatch(updateContentList(searchResponse));
        ShowToastSuccess(`${t("page")} ${t("deleted_toast")}`);
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;

        ShowToastError(t("api_error_toast"), statusCode);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**unpublish page */
  const unPublishPage = (
    selectedPageDataProp: any,
    isDelete: boolean = false,
    filterObject: any = {},
  ) => {
    const { filter: filterType = "ALL" } = filterObject || {};

    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    mutateUnpublish({
      variables: {
        page: selectedPageDataProp.page,
        currentpageurl: selectedPageDataProp.currentPageUrl,
        parentpageurl: selectedPageDataProp.parentPageUrl,
        timeZone: timeZone,
      },
    })
      .then(async () => {
        ShowToastSuccess(t("unpublish_toast"));
        if (!isDelete) {
          const searchResponse = await contentTypeAPIs.fetchSearchContent(
            capitalizeFirstLetter("sitepage"),
            location,
            filterType,
            startIndex,
            contentList,
            true,
          );
          dispatch(updateContentList(searchResponse));
        } else {
          handleRemove(selectedPageDataProp, filterObject);
        }
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;

        ShowToastError(t("api_error_toast"), statusCode);
      });
  };

  /**handle page delete conditions main */
  const handlePageDelete = (selectedPage, filterObject = {}) => {
    try {
      setLoading(true);
      const requestDto = {
        page: selectedPage.page,
        currentpageurl: selectedPage.currentPageUrl,
        parentpageurl: selectedPage.parentPageUrl,
      };
      if (selectedPage?.status === "published") {
        if (selectedPage?.scheduledUnPublishTriggerDateTime != null) {
          cancelPublishUnpublishTrigger("2", requestDto, selectedPage);
        } else {
          unPublishPage(selectedPage, true, filterObject);
        }
      } else if (selectedPage?.scheduledPublishTriggerDateTime != null) {
        cancelPublishUnpublishTrigger("1", requestDto, selectedPage);
      } else {
        handleRemove(selectedPage, filterObject);
      }
    } catch (e) {
      console.error("error in handlePageDelete", e);
    }
  };

  /**handle reschedule popup */
  const handleReschedulePopup = useCallback(
    (
      //  type: any,
      itemsdata: {
        path: string;
        page: any;
        currentPageUrl: any;
        parentPageUrl: any;
        scheduledUnPublishTriggerDateTime: SetStateAction<string>;
        scheduledPublishTriggerDateTime: SetStateAction<string>;
      },
    ) => {
      const arr = itemsdata.path?.split("/");
      // const folder = arr[5];
      // const pathnm = `${arr[6]}/${arr[7]}`;
      // const folder = arr[6];
      // const pathnm = arr[10];
      const [folder, pathnm] = arr;
      runFetchPageModel({
        variables: { folder: folder, path: pathnm },
      })
        .then(() => {})
        .catch(() => {});
      setCurrentUnpublishTime(itemsdata.scheduledUnPublishTriggerDateTime);
      setCurrentPublishTime(itemsdata.scheduledPublishTriggerDateTime);
    },
    [],
  );

  /**reschedule publish page */
  const reschedulePublishPage = (publishTime: string | number | Date, rescheduleDto: any) => {
    const currentDateTime = addMinutes(new Date(), 5);
    const enteredPublishTime = new Date(publishTime);
    const validPublishTime =
      currentUnpublishTime != null ? addMinutes(new Date(currentUnpublishTime), 10) : null;
    if (
      validPublishTime != null &&
      new Date(currentUnpublishTime) < addMinutes(enteredPublishTime, 10)
    ) {
      ShowToastError(`${t("page")} ${t("scheduled_publish_time")}`);
    } else {
      if (enteredPublishTime > currentDateTime) {
        mutatePublishSchedule({
          variables: {
            requestdto: rescheduleDto,
            scheduleTime: publishTime?.toString(),
          },
        })
          .then(async () => {
            const searchResponse = await contentTypeAPIs.fetchSearchContent(
              capitalizeFirstLetter("sitepage"),
              location,
              filter,
              startIndex,
              contentList,
              true,
            );
            dispatch(updateContentList(searchResponse));
            ShowToastSuccess(`${t("page")} ${t("publish")} ${t("rescheduled_success_toast")}`);
          })
          .catch((error: any) => {
            const statusCode = error?.networkError?.statusCode || 0;
            ShowToastError(t("api_error_toast"), statusCode);
          });
      } else {
        ShowToastError(`${t("page")} ${t("publish_time_toast")}`);
      }
    }
  };

  const publishPage = () => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const newModel = consolidatePageModel(
      page.pageInfo?.pageModel,
      page.pageInfo?.prelemMetaArray,
      page.pageInfo?.pageSettings,
      username,
    );
    const requestdto = {
      page: page.pageInfo?.pageModel.Page,
      parentpageurl: "/",
      currentpageurl: page.pageInfo?.pageModel.CurrentPageURL,
    };
    mutatePublish({
      variables: {
        input: requestdto,
        pageModelRequest: newModel,
        timeZone: timeZone,
      },
    })
      .then(() => {
        ShowToastSuccess(`${t("page")} ${t("pubished_success_toast")}`);
        const pageDataObj = {
          eventType: "Page Published",
          pagePublished: true,
        };
        handleImpression(pageDataObj.eventType, pageDataObj);
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(`${t("page")} ${t("published_error_toast")}`, statusCode);
      });
  };

  /**reschedule unpublish page */
  const rescheduleUnPublishPage = (
    unpublishTime: string | number | Date,
    rescheduleDto: any,
    listItemDetails: { status: string },
  ) => {
    const currentDateTime = addMinutes(new Date(), 5);
    const enteredUnpublishTime = new Date(unpublishTime);
    const validPublishTime =
      currentPublishTime !== "" ? addMinutes(new Date(currentPublishTime), 10) : null;
    if (validPublishTime != null && validPublishTime > enteredUnpublishTime) {
      ShowToastError(`${t("page")} ${t("publish_vs_unpublish_toast")}`);
    } else if (enteredUnpublishTime > currentDateTime) {
      listItemDetails?.status.toLowerCase() === "published" && publishPage();
      mutateUnpublishSchedule({
        variables: {
          requestdto: rescheduleDto,
          scheduleTime: unpublishTime,
        },
      })
        .then(async () => {
          const searchResponse = await contentTypeAPIs.fetchSearchContent(
            capitalizeFirstLetter("sitepage"),
            location,
            filter,
            startIndex,
            contentList,
            true,
          );
          dispatch(updateContentList(searchResponse));
          ShowToastSuccess(`${t("page")} ${t("unpublish")} ${t("rescheduled_success_toast")}`);
        })
        .catch((error: any) => {
          const statusCode = error?.networkError?.statusCode || 0;
          ShowToastError(t("api_error_toast"), statusCode);
        });
    } else {
      ShowToastError(`${t("page")} ${t("unpublish_time_toast")}`);
    }
  };

  /**handle cancel publish/unpublish popup */
  const handleCancelTriggerPopup = (itemsdata: {
    path: string;
    page: any;
    currentPageUrl: any;
    parentPageUrl: any;
  }) => {
    const arr = itemsdata.path?.split("/");
    const [folder, pathnm] = arr;
    runFetchPageModel({
      variables: { folder: folder, path: pathnm },
    })
      .then(() => {})
      .catch(() => {});
  };

  /**cancel publish/unpublish page */
  const cancelPublishUnpublishTrigger = (
    triggerType: string,
    requestDto: { page: any; currentpageurl: any; parentpageurl: any },
    listItemDetails: {
      scheduledUnPublishTriggerDateTime: null;
      status: string;
      page: any;
      currentPageUrl: any;
      parentPageUrl: any;
    },
    isDelete: boolean = false,
  ) => {
    if (triggerType === "1") {
      mutateCancelPublishSchedule({
        variables: { requestdto: requestDto },
      })
        .then(() => {
          ShowToastSuccess(`${t("page")} ${t("publish")} ${t("schedule_cancel_toast")}`);
          if (isDelete) {
            handleRemove(listItemDetails);
          }
        })
        .catch((error: any) => {
          const statusCode = error?.networkError?.statusCode || 0;
          ShowToastError(t("api_error_toast"), statusCode);
        });
    }
    if (
      (triggerType === "1" &&
        listItemDetails &&
        listItemDetails?.scheduledUnPublishTriggerDateTime) ||
      triggerType === "2"
    ) {
      mutateCancelUnpublishSchedule({
        variables: { requestdto: requestDto },
      })
        .then(() => {
          ShowToastSuccess(`${t("page")} ${t("unpublish")} ${t("schedule_cancel_toast")}`);
          if (isDelete) {
            if (listItemDetails?.status !== "published") {
              handleRemove(listItemDetails);
            } else {
              unPublishPage(listItemDetails, true);
            }
          }
        })
        .catch((error: any) => {
          const statusCode = error?.networkError?.statusCode || 0;
          ShowToastError(t("api_error_toast"), statusCode);
        });
    }
  };

  /** Fetches the page's current brand_name / secondary_brandname for dialog preselection. */
  const fetchPageBrands = (selectedPage: any) => brandsApi.getPageBrands(selectedPage?.path);

  const sharePageWithBrands = async (
    selectedPage: any,
    primaryBrandId: string,
    selectedBrandIds: string[],
  ) => {
    try {
      setLoading(true);
      const result = await brandsApi.sharePageWithBrands(
        selectedPage?.path,
        primaryBrandId,
        selectedBrandIds,
        username,
        // A live page (published, or published with newer draft changes) is published again so the
        // new brand takes effect on the site. A page with a pending (future) publish/unpublish
        // schedule is left to the editor, whose schedule logic a plain re-publish would bypass —
        // same rule as content types. A schedule that already fired is history, not pending.
        (selectedPage?.is_published === true ||
          String(selectedPage?.status).toLowerCase() === "published") &&
          !isScheduledInFuture(selectedPage?.scheduledPublishTriggerDateTime) &&
          !isScheduledInFuture(selectedPage?.scheduledUnPublishTriggerDateTime),
      );
      ShowToastSuccess(`${selectedPage?.title} ${t("shared_toast")}`);
      if (result?.publishError) {
        // The share is saved (as a draft revision); only the re-publish failed.
        ShowToastError(`${t("api_error_toast")}: ${result.publishError}`);
      }
      // Same follow-up the other page actions do (unpublish, delete…): the listing row and — when
      // this page is the one already loaded in the editor — the editor's copy go stale otherwise
      // (old brand, URLs and state). Content types get this from the container's refresh.
      try {
        const searchResponse = await contentTypeAPIs.fetchSearchContent(
          capitalizeFirstLetter("sitepage"),
          location,
          filter,
          startIndex,
          contentList,
          true,
        );
        dispatch(updateContentList(searchResponse));
        if (page?.pageModel?.Path && page.pageModel.Path === selectedPage?.path) {
          await fetchPageModel(dispatch, client, selectedPage.path);
        }
      } catch (refreshError) {
        // The share itself is saved; a failed refresh only means the list refreshes on next load.
        console.error("sharePageWithBrands: refresh failed", refreshError);
      }
      return result;
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;
      ShowToastError(
        error?.graphQLErrors?.[0] ? error.graphQLErrors[0].message : t("api_error_toast"),
        statusCode,
      );
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchPageBrands,
    sharePageWithBrands,
    editPage,
    viewPage,
    previewPage,
    handleDuplicatePopup,
    duplicatePage,
    unPublishPage,
    handleReschedulePopup,
    reschedulePublishPage,
    rescheduleUnPublishPage,
    handleCancelTriggerPopup,
    cancelPublishUnpublishTrigger,
    handlePageDelete,
    loading,
  };
};

export default usePage;
